import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  NotificationsApi,
  type UserNotification,
} from '@up2stay/up2promo-api-client';

import { usePendingInvitations } from '@/core/hooks/use-pending-invitations';
import { useCurrentWorkspace } from '@/core/store/workspace.store';

export const notificationInboxQueryKey = (
  workspaceId: string | undefined,
  unreadOnly = true,
) => ['notifications', 'inbox', workspaceId, unreadOnly] as const;

export const notificationUnreadCountQueryKey = (
  workspaceId: string | undefined,
) => ['notifications', 'unread-count', workspaceId] as const;

export function useNotificationInbox(options?: { unreadOnly?: boolean }) {
  const workspace = useCurrentWorkspace();
  const workspaceId = workspace?.id;
  const unreadOnly = options?.unreadOnly ?? true;

  return useQuery({
    queryKey: notificationInboxQueryKey(workspaceId, unreadOnly),
    enabled: Boolean(workspaceId),
    queryFn: async (): Promise<UserNotification[]> => {
      const response = await NotificationsApi.listInbox({
        path: { workspaceId: workspaceId! },
        ...(unreadOnly ? { query: { unreadOnly: 'true' } } : {}),
      } as never);
      return response.data ?? [];
    },
  });
}

export function useUnreadNotificationCount() {
  const workspace = useCurrentWorkspace();

  return useQuery({
    queryKey: notificationUnreadCountQueryKey(workspace?.id),
    enabled: Boolean(workspace?.id),
    queryFn: async (): Promise<number> => {
      const response = await NotificationsApi.unreadCount({
        path: { workspaceId: workspace!.id },
      });
      return response.data?.count ?? 0;
    },
    refetchInterval: 60_000,
  });
}

/** Pending workspace invitations + unread in-app notifications (nav badges). */
export function useNotificationHubBadgeCount() {
  const { data: pendingInvitations } = usePendingInvitations();
  const { data: unreadNotifications = 0 } = useUnreadNotificationCount();
  const pendingCount = pendingInvitations?.length ?? 0;
  const badgeCount = pendingCount + unreadNotifications;

  return { badgeCount, pendingCount, unreadCount: unreadNotifications };
}

export function useNotificationMutations() {
  const workspace = useCurrentWorkspace();
  const queryClient = useQueryClient();
  const workspaceId = workspace?.id;

  const invalidate = async () => {
    if (!workspaceId) return;
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ['notifications', 'inbox', workspaceId],
      }),
      queryClient.invalidateQueries({
        queryKey: notificationUnreadCountQueryKey(workspaceId),
      }),
    ]);
  };

  const markRead = async (notificationId: string) => {
    if (!workspaceId) return;
    await NotificationsApi.markRead({
      path: { workspaceId, notificationId },
    });
    await invalidate();
  };

  const markAllRead = async () => {
    if (!workspaceId) return;
    await NotificationsApi.markAllRead({
      path: { workspaceId },
    });
    await invalidate();
  };

  return { markRead, markAllRead, invalidate };
}
